const SELECTORS = {
    block: '.wp-block-ssm-accordion',
    item: '.wp-block-ssm-accordion__item',
    trigger: '[data-accordion-trigger]',
    triggerInner: '.wp-block-ssm-accordion__trigger-inner',
    panel: '[data-accordion-panel]',
};

const CLASSES = {
    open: 'is-open',
};

const PANEL_MARGIN = 50;
const MIN_EXPANDABLE_HEIGHT = 380;
const MIN_CALCULATED_HEIGHT = 150; // Threshold below which we use MIN_EXPANDABLE_HEIGHT
const RESIZE_DEBOUNCE_MS = 200;

// Complement of the theme's `--md` breakpoint, where `.wp-block-columns` stacks
// (see blocks/columns.css). Once stacked, the block's parent is content-driven
// and can no longer be used as an available-space reference.
const STACKED_LAYOUT_QUERY = 'not all and (min-width: 48rem)';

const HEIGHT_VARIABLE = '--expandable-content-height';

// Breathing room left above a freshly opened item when it is scrolled into view
const SCROLL_GAP = 16;

/**
 * Calculate available height for expanded panel content.
 * Batches all DOM reads before writes to avoid layout thrashing.
 *
 * @param {HTMLElement} block - The accordion container
 * @param {Object} cache - Cached element references
 * @param {HTMLElement|null} changedItem - Optional: only recalculate for this item's change
 */
const calculateExpandableHeight = (block, cache, changedItem = null) => {
    let totalTriggerHeight = 0;

    cache.triggerInners.forEach((inner) => {
        totalTriggerHeight += inner.offsetHeight;
    });

    const borderHeight = cache.itemCount > 0 ? 2 + (cache.itemCount - 1) * 2 : 0;
    totalTriggerHeight += borderHeight;

    // Use the block's *parent* as the available space reference,
    // not the block itself — block height is unreliable when panels are open
    const availableHeight = block.parentElement.offsetHeight;

    let expandableHeight = availableHeight - totalTriggerHeight - PANEL_MARGIN;

    // Only fall back if the parent itself is weirdly small (e.g. not yet laid out)
    if (expandableHeight < MIN_CALCULATED_HEIGHT) {
        expandableHeight = MIN_EXPANDABLE_HEIGHT;
    }

    if (cache.lastExpandableHeight !== expandableHeight) {
        cache.lastExpandableHeight = expandableHeight;
        block.style.setProperty(HEIGHT_VARIABLE, `${expandableHeight}px`);
    }

    return expandableHeight;
};

/**
 * Stacked layout: cap each open panel at its own content height.
 *
 * The calculation above is circular here — the parent has no height of its own,
 * so it grows with the panel we are sizing and the value never settles. Content
 * height is independent of the cap, so it converges on the first pass and the
 * max-height transition still has a concrete target to animate to.
 *
 * The variable is set on the panel itself so items can differ in height when
 * multiple are open; it shadows the block-level value.
 *
 * @param {HTMLElement} block - The accordion container
 * @param {Object} cache - Cached element references
 */
const applyNaturalPanelHeights = (block, cache) => {
    if (cache.lastExpandableHeight !== null) {
        cache.lastExpandableHeight = null;
        block.style.removeProperty(HEIGHT_VARIABLE);
    }

    cache.items.forEach((item) => {
        const panel = cache.panelMap.get(item);
        if (!panel) return;

        if (!item.classList.contains(CLASSES.open)) {
            panel.style.removeProperty(HEIGHT_VARIABLE);

            return;
        }

        // scrollHeight is rounded, so a fractional content height would leave a
        // sliver below the cap — with overflow hidden that clips instead of scrolls
        const content = cache.contentMap.get(item);
        const measured = content
            ? Math.max(panel.scrollHeight, Math.ceil(content.getBoundingClientRect().height))
            : panel.scrollHeight;

        const height = `${measured}px`;

        if (panel.style.getPropertyValue(HEIGHT_VARIABLE) !== height) {
            panel.style.setProperty(HEIGHT_VARIABLE, height);
        }
    });
};

/**
 * Document-space position the item's top will settle at once the items above it
 * collapse.
 *
 * Must be called *before* the open/close classes are toggled, while the panels
 * that are about to close still report their open heights — their max-height
 * transition only starts on the next frame, so measuring afterwards would read
 * the pre-collapse layout and land the scroll too low.
 *
 * @param {HTMLElement} item - The item being opened
 * @param {Object} cache - Cached element references
 * @param {boolean} allowMultiple - Whether siblings stay open
 */
const measureOpenTargetTop = (item, cache, allowMultiple) => {
    const documentTop = item.getBoundingClientRect().top + window.scrollY;

    if (allowMultiple) return documentTop;

    let collapseShift = 0;

    // Only the open items *above* this one move it up when they close
    for (const sibling of cache.items) {
        if (sibling === item) break;
        if (!sibling.classList.contains(CLASSES.open)) continue;

        const panel = cache.panelMap.get(sibling);
        if (!panel) continue;

        const marginBottom = parseFloat(window.getComputedStyle(panel).marginBottom) || 0;
        collapseShift += panel.getBoundingClientRect().height + marginBottom;
    }

    return documentTop - collapseShift;
};

/**
 * Stacked layout: bring the opened item to the top of the viewport so its panel
 * content starts in view instead of below the fold.
 *
 * @param {number} documentTop - Target from `measureOpenTargetTop`
 */
const scrollToOpenedItem = (documentTop) => {
    // The site header slides out of the way on downward scrolls, so only reserve
    // room for it when the page is heading up and the header stays put.
    const isScrollingUp = documentTop < window.scrollY;
    const headerHeight = isScrollingUp ? document.querySelector('.site-header')?.offsetHeight || 0 : 0;

    const target = Math.max(0, documentTop - headerHeight - SCROLL_GAP);

    // Lenis owns the window scroll, so go through it — a native scroll would
    // fight its RAF loop and skip ScrollTrigger.update.
    if (window.lenisInstance) {
        window.lenisInstance.scrollTo(target);
    } else {
        window.scrollTo({ top: target, behavior: 'smooth' });
    }
};

/**
 * Hand sizing back to the block-level variable (unstacked layout).
 */
const clearNaturalPanelHeights = (cache) => {
    cache.panelMap.forEach((panel) => {
        panel.style.removeProperty(HEIGHT_VARIABLE);
    });
};

const setItemState = (item, isOpen, cache) => {
    if (!item || !cache) return;

    const trigger = cache.triggerMap.get(item);
    const panel = cache.panelMap.get(item);

    // When closing with an active scrollbar, keep overflow:auto for the duration of the
    // close animation so the scrollbar disappears naturally without a layout shift.
    // When closing without overflow, overflow:hidden kicks in immediately (no stray scrollbar).
    if (!isOpen && panel) {
        const computedOverflow = window.getComputedStyle(panel).overflow;
        if (computedOverflow === 'auto' && panel.scrollHeight > panel.clientHeight) {
            panel.style.overflow = 'auto';
            panel.style.scrollbarGutter = 'stable';
            setTimeout(() => {
                panel.style.removeProperty('overflow');
                panel.style.removeProperty('scrollbar-gutter');
            }, 600);
        }
    }

    item.classList.toggle(CLASSES.open, isOpen);
    item.dataset.open = isOpen ? 'true' : 'false';

    if (trigger) {
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }

    if (panel) {
        panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

        if (isOpen) {
            panel.removeAttribute('inert');
        } else {
            panel.setAttribute('inert', '');
        }
    }
};

const buildCache = (block, items) => {
    const triggerInners = Array.from(block.querySelectorAll(SELECTORS.triggerInner));
    const triggerMap = new Map();
    const panelMap = new Map();
    const contentMap = new Map();

    items.forEach((item) => {
        const trigger = item.querySelector(SELECTORS.trigger);
        const panel = item.querySelector(SELECTORS.panel);
        if (trigger) triggerMap.set(item, trigger);
        if (panel) panelMap.set(item, panel);
        if (panel?.firstElementChild) contentMap.set(item, panel.firstElementChild);
    });

    return {
        items,
        itemCount: items.length,
        triggerInners,
        triggerMap,
        panelMap,
        contentMap,
        lastExpandableHeight: null,
    };
};

export const initAccordion = (block) => {
    if (!block || block.dataset.accordionReady) return null;

    const items = Array.from(block.querySelectorAll(SELECTORS.item));
    if (!items.length) return null;

    const allowMultiple = block.dataset.allowMultiple === 'true';
    const openFirst = block.dataset.openFirst !== 'false';

    block.dataset.accordionReady = 'true';

    const cache = buildCache(block, items);

    const stackedQuery = window.matchMedia(STACKED_LAYOUT_QUERY);

    // Shared state object to avoid stale closures
    const state = {
        resizeTimeout: null,
        rafId: null,
        isDestroyed: false,
    };

    // Cancels any pending async operations
    const cancelPending = () => {
        if (state.resizeTimeout) {
            clearTimeout(state.resizeTimeout);
            state.resizeTimeout = null;
        }
        if (state.rafId) {
            cancelAnimationFrame(state.rafId);
            state.rafId = null;
        }
    };

    // Size panels for the current layout
    const applyHeights = (changedItem = null) => {
        if (stackedQuery.matches) {
            applyNaturalPanelHeights(block, cache);
        } else {
            clearNaturalPanelHeights(cache);
            calculateExpandableHeight(block, cache, changedItem);
        }
    };

    // Schedule a height calculation, canceling any pending one
    const scheduleCalculation = (changedItem = null) => {
        if (state.isDestroyed) return;

        cancelPending();
        state.rafId = requestAnimationFrame(() => {
            if (!state.isDestroyed) {
                applyHeights(changedItem);
            }
        });
    };

    // Debounced handler for resize events
    const handleResize = () => {
        if (state.isDestroyed) return;

        cancelPending();
        state.resizeTimeout = setTimeout(() => {
            scheduleCalculation();
        }, RESIZE_DEBOUNCE_MS);
    };

    // Re-measure from scratch when the layout stacks or unstacks
    const handleLayoutChange = () => {
        if (state.isDestroyed) return;

        cancelPending();

        cache.lastExpandableHeight = null;
        block.style.removeProperty(HEIGHT_VARIABLE);
        clearNaturalPanelHeights(cache);

        scheduleCalculation();
    };

    stackedQuery.addEventListener('change', handleLayoutChange);

    // Observe block container instead of individual triggers
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(block);

    // While stacked, panels are capped at their content height, so late reflow
    // inside a panel (images, fonts) never changes the block's own size —
    // observe the content itself to pick those up
    cache.contentMap.forEach((content) => resizeObserver.observe(content));

    // Initial calculation after layout is complete
    scheduleCalculation();

    const closeSiblings = (current) => {
        if (allowMultiple) return;

        cache.items.forEach((item) => {
            if (item !== current) {
                setItemState(item, false, cache);
            }
        });
    };

    const toggleItem = (item) => {
        if (state.isDestroyed) return;

        const isOpen = item.classList.contains(CLASSES.open);
        const nextState = !isOpen;
        const isStacked = stackedQuery.matches;

        // Measured up front, before the classes below change the layout
        const targetTop = isStacked && nextState ? measureOpenTargetTop(item, cache, allowMultiple) : null;

        if (nextState) {
            closeSiblings(item);
        }

        setItemState(item, nextState, cache);

        if (isStacked) {
            // Measure before the next paint so the opening transition animates
            // toward its final height instead of the CSS fallback
            cancelPending();
            applyNaturalPanelHeights(block, cache);

            if (targetTop !== null) {
                scrollToOpenedItem(targetTop);
            }

            return;
        }

        // Cancel any pending calculation and schedule new one
        scheduleCalculation(item);
    };

    const triggerHandlers = [];

    items.forEach((item, index) => {
        const trigger = cache.triggerMap.get(item);

        const shouldOpen = item.dataset.open === 'true' || item.classList.contains(CLASSES.open) || (openFirst && index === 0);

        setItemState(item, shouldOpen, cache);

        if (trigger) {
            const handler = () => toggleItem(item);
            trigger.addEventListener('click', handler);
            triggerHandlers.push({ trigger, handler });
        }
    });

    if (!allowMultiple) {
        const openItems = items.filter((item) => item.classList.contains(CLASSES.open));
        if (openItems.length > 1) {
            openItems.slice(1).forEach((item) => setItemState(item, false, cache));
        }
    }

    return {
        destroy() {
            // Mark as destroyed first to prevent any new async work
            state.isDestroyed = true;

            // Cancel all pending async operations
            cancelPending();

            // Disconnect observer
            resizeObserver.disconnect();
            stackedQuery.removeEventListener('change', handleLayoutChange);

            // Remove event listeners
            triggerHandlers.forEach(({ trigger, handler }) => {
                trigger.removeEventListener('click', handler);
            });

            // Reset DOM state
            cache.items.forEach((item) => {
                item.classList.remove(CLASSES.open);
                const panel = cache.panelMap.get(item);
                if (panel) {
                    panel.setAttribute('aria-hidden', 'true');
                    panel.setAttribute('inert', '');
                }
                const trigger = cache.triggerMap.get(item);
                if (trigger) trigger.setAttribute('aria-expanded', 'false');
            });

            block.style.removeProperty(HEIGHT_VARIABLE);
            clearNaturalPanelHeights(cache);
            delete block.dataset.accordionReady;
        },
    };
};

export const Accordion = () => {
    document.querySelectorAll(SELECTORS.block).forEach((block) => {
        initAccordion(block);
    });
};
